import { useMemo } from 'react'

import { RefetchDevicesFunction, useFloorPlan } from '../../hooks/useFloorPlan'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'

import {
  DarkTailgateGridModal,
  TailgateGridModal,
  TailgateGridProvider,
} from '@/features/tailgate-grid'
import { UpdateTailgateEnabledInput } from '@/graphql/generated/schemas'
import { BUTTON_PRESS } from '@/utils/analytics'

import { ConnectionI, DeviceI } from '../../types/types'
import { DarkAssociateDeviceModal } from '../dark/DarkAssociateDeviceModal'
import { getById } from '../utils/utils'
import { AssociateDeviceModal } from './AssociateDeviceModal'

interface TailgateConnectionModalsIProps {
  connections: ConnectionI[]
  isConnectionsLoading: boolean
  connectDevices: (
    camera: DeviceI,
    door: DeviceI,
    refetchDevices: RefetchDevicesFunction
  ) => Promise<void>
  disconnectDevices: (
    cameraId: string,
    doorId: string,
    refetchDevices: RefetchDevicesFunction
  ) => Promise<void>
  getConnection: (cameraId: string, doorId: string) => ConnectionI
}

export const TailgateConnectionModals = ({
  connections,
  isConnectionsLoading,
  connectDevices,
  disconnectDevices,
  getConnection,
}: TailgateConnectionModalsIProps) => {
  const isFacilitiesRebrandEnabled = useFeatureFlag(
    FeatureFlag.facilitiesRebrand
  )
  const {
    associateDeviceValues,
    devices,
    isAssociateDeviceOpen,
    isTailgateGridOpen,
    isEnableTailgateLoading,
    enableTailgate,
    onAssociateDeviceClose,
    onTailgateGridClose,
    onTailgateGridOpen,
    refetchDevices,
    trackTailgateEvent,
  } = useFloorPlan()

  const handleOnTailgateGridOpen = () => {
    onTailgateGridOpen()
    trackTailgateEvent(`${BUTTON_PRESS} Configure Tailgate grid`)
  }

  const disassociateSelectedDevices = async () => {
    const { doorId, cameraId } = associateDeviceValues
    await disconnectDevices(cameraId, doorId, refetchDevices)
    trackTailgateEvent(`${BUTTON_PRESS} Disassociate devices`)
  }

  const associateSelectedDevices = async () => {
    const { cameraId, doorId } = associateDeviceValues
    const alreadyConnected = getConnection(cameraId, doorId)
    if (alreadyConnected) return

    const camera = getById(devices, cameraId)
    const door = getById(devices, doorId)
    await connectDevices(camera, door, refetchDevices)
    trackTailgateEvent(`${BUTTON_PRESS} Associate devices`)
  }

  const handleEnableTailgate = async (isTailgateEnabled: boolean) => {
    const { doorId, cameraId } = associateDeviceValues
    const input: UpdateTailgateEnabledInput = {
      cameraId,
      doorId,
      isTailgateEnabled,
    }
    await enableTailgate(input, refetchDevices)
  }

  const connection = useMemo(
    () =>
      getConnection(
        associateDeviceValues?.cameraId,
        associateDeviceValues?.doorId
      ),
    [connections, associateDeviceValues]
  )

  const shouldRenderDummyPlayer = isTailgateGridOpen

  // The association flow is the flag-once root for its two modals: reading the
  // flag here keeps the rebranded pair together instead of switching each mount.
  const AssociateDeviceModalForBranch = isFacilitiesRebrandEnabled
    ? DarkAssociateDeviceModal
    : AssociateDeviceModal
  const TailgateGridModalForBranch = isFacilitiesRebrandEnabled
    ? DarkTailgateGridModal
    : TailgateGridModal

  return (
    <>
      {isAssociateDeviceOpen && (
        <AssociateDeviceModalForBranch
          associateDevice={associateDeviceValues}
          connection={connection}
          hideAssociateButton={false}
          isEnableTailgateLoading={isEnableTailgateLoading}
          isOpen={isAssociateDeviceOpen}
          loading={isConnectionsLoading}
          onAssociate={associateSelectedDevices}
          onClose={onAssociateDeviceClose}
          onDisassociate={disassociateSelectedDevices}
          onEnableTailgate={handleEnableTailgate}
          onOpenGrid={handleOnTailgateGridOpen}
        />
      )}
      <TailgateGridProvider
        associateDeviceValues={associateDeviceValues}
        isGridConfigured={connection?.isTailgateGridConfigured}
        isTailgateGridOpen={isTailgateGridOpen}
        onTailgateGridClose={onTailgateGridClose}
        shouldRenderDummyPlayer={shouldRenderDummyPlayer}
        trackTailgateEvent={trackTailgateEvent}
      >
        <TailgateGridModalForBranch />
      </TailgateGridProvider>
    </>
  )
}
